import React, { useCallback, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { useDevToolsStore } from '../../store/devToolsStore';
import { isTauri } from '../../utils/tauri';
import { MONACO_LAYOUT_FLUSH_EVENT } from '../ui/ResizableSplitter';
import ConsolePanel from './ConsolePanel';
import PerformancePanel from './PerformancePanel';

// The terminal moved into the Terminal extension panel (SSH + local tabs) —
// the bottom devtools panel only hosts Console and Performance now.
const TAB_LABELS = { console: 'Console', performance: 'Performance' };

export default function DevToolsPanel({ workspaceRootPath }) {
  const isOpen = useDevToolsStore((s) => s.isOpen);
  const activeTab = useDevToolsStore((s) => s.activeTab);
  const close = useDevToolsStore((s) => s.close);
  const panelHeight = useDevToolsStore((s) => s.panelHeight);
  const setPanelHeight = useDevToolsStore((s) => s.setPanelHeight);

  const desktop = isTauri();
  const [isResizing, setIsResizing] = useState(false);
  const panelHeightRef = useRef(panelHeight);

  panelHeightRef.current = panelHeight;

  const handleResizeStart = useCallback(
    (e) => {
      e.preventDefault();
      setIsResizing(true);
      const startY = e.clientY;
      const startH = panelHeightRef.current;

      const onMove = (ev) => {
        const delta = startY - ev.clientY;
        setPanelHeight(startH + delta);
        window.dispatchEvent(new CustomEvent(MONACO_LAYOUT_FLUSH_EVENT));
      };

      const onUp = () => {
        setIsResizing(false);
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
        window.dispatchEvent(new CustomEvent(MONACO_LAYOUT_FLUSH_EVENT));
      };

      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
      document.body.style.cursor = 'row-resize';
      document.body.style.userSelect = 'none';
    },
    [setPanelHeight]
  );

  if (!isOpen) return null;

  return (
    <div
      className="devtools-panel flex-shrink-0 flex flex-col min-h-0"
      style={{ height: panelHeight }}
    >
      <div
        role="separator"
        aria-orientation="horizontal"
        aria-label="Resize Dev Tools panel"
        className={`resizer resizer--vertical flex-shrink-0 w-full${isResizing ? ' dragging' : ''}`}
        onMouseDown={handleResizeStart}
      />
      <div className="devtools-panel__header">
        <span className="devtools-panel__tab-label">{TAB_LABELS[activeTab] || activeTab}</span>
        <div className="devtools-panel__header-actions">
          <button type="button" className="devtools-panel__close" onClick={close} title="Close">
            <X size={13} />
          </button>
        </div>
      </div>
      <div className="devtools-panel__body flex flex-1 min-h-0 flex-col">
        <div
          className="devtools-tab-panel flex flex-1 min-h-0 flex-col"
          style={{ display: activeTab === 'console' ? 'flex' : 'none' }}
        >
          <ConsolePanel />
        </div>
        {desktop && (
          <div
            className="devtools-tab-panel flex flex-1 min-h-0 flex-col"
            style={{ display: activeTab === 'performance' ? 'flex' : 'none' }}
          >
            <PerformancePanel active={activeTab === 'performance'} />
          </div>
        )}
      </div>
    </div>
  );
}
